import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import { chatApi } from '../services/api/chatApi';

export function useChatMessagesQuery(sessionId: string | null) {
  return useQuery({
    queryKey: queryKeys.chat.messages(sessionId!),
    queryFn: () => chatApi.getChatMessages(sessionId!),
    enabled: !!sessionId,
    staleTime: 10_000,
  });
}

export function useMatterChatSessionsQuery(matterId: string | null) {
  return useQuery({
    queryKey: queryKeys.matters.chatSessions(matterId!),
    queryFn: () => chatApi.getMatterChatSessions(matterId!),
    enabled: !!matterId,
  });
}

/**
 * Plan 174 (#763): bucketed scoped-chat user-turn counts for the matter.
 * One call per matter visit; consumers (ClauseCard / SectionCard) read the
 * `clause` bucket via `useClauseMessageCountsMap`. Issue/finding buckets
 * ship in the payload for a future follow-up that wires those surfaces
 * with no further backend work.
 */
export function useScopedChatCountsQuery(matterId: string | null) {
  return useQuery({
    queryKey: queryKeys.matters.scopedChatCounts(matterId!),
    queryFn: () => chatApi.getScopedChatMessageCounts(matterId!),
    enabled: !!matterId,
    staleTime: 30_000,
  });
}

// Stable reference returned during loading / empty states. Without this, every
// render produces a new Map → consumers' useMemo deps invalidate → every
// ClauseCard in the document re-renders on each fetch tick. (Plan 174 P2 review.)
const EMPTY_CLAUSE_COUNTS: ReadonlyMap<string, number> = Object.freeze(new Map());

/**
 * Selector returning the `clause` bucket as a stable Map<clauseId, count>.
 * Identity is preserved across renders when the underlying data is unchanged
 * (TanStack Query's structural sharing keeps `data.clause` referentially
 * equal across refetches when the payload is identical), so downstream
 * `useMemo` dependencies don't churn.
 *
 * NB: the `clause` bucket is keyed by `chat_sessions.source_id`, which for
 * clause-scoped sessions is the logical clause-id form (e.g.
 * "2736eff07b:1_3:b0347feda4"), NOT the database UUID. Consumers should do
 * `map.get(clause.id) ?? map.get(clause.clause_id ?? '')` to be safe across
 * both id forms.
 */
export function useClauseMessageCountsMap(matterId: string | null): ReadonlyMap<string, number> {
  const { data } = useScopedChatCountsQuery(matterId);
  return useMemo<ReadonlyMap<string, number>>(() => {
    if (!data?.clause) return EMPTY_CLAUSE_COUNTS;
    const entries = Object.entries(data.clause);
    if (entries.length === 0) return EMPTY_CLAUSE_COUNTS;
    return new Map(entries);
  }, [data?.clause]);
}
